import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {modalShow} from '@core/actions/ModalActions';
import CareerLevel from './fields/CareerLevel';
import Urgency from './fields/Urgency';
import ServicesGrid from './fields/ServicesGrid';
import FeaturesGrid from './fields/FeaturesGrid';
import Tooltip from './fields/Tooltip';
import {formatPrice} from '../helpers/PriceHelper';
import AnimatedNumber from '../../common/AnimatedNumber';

function Section({icon, title, sub, count, extraClass = '', innerRef, children}) {
    return (
        <div ref={innerRef} className={`resume-form__section ${extraClass}`}>
            <div className="resume-form__section-header">
                <div className="resume-form__section-icon">{icon}</div>
                <div className="resume-form__section-head-text">
                    <div className="resume-form__section-title">
                        {title}
                        {count > 0 && <span className="resume-form__section-count">{count}</span>}
                    </div>
                    {sub && <div className="resume-form__section-sub">{sub}</div>}
                </div>
            </div>
            <div className="resume-form__section-body">
                {children}
            </div>
        </div>
    );
}

export default function OrderDetailsPage({options, calculator, hasError, servicesRef, selectedServices = [], selectedFeatures = [], isAccountPage = false, fileUploadRef, onOpenFileUpload}) {
    const {services, features, totalPrice} = calculator;
    const dispatch = useDispatch();
    const files = useSelector(state => state.file.data) || [];
    const filesRequired = selectedFeatures.some(key => options.features?.[key]?.fileRequired);
    const hasFileError = filesRequired && files.length === 0;

    return <div className="resume-form__step-one">
        <div className="resume-form__page-header">
            {!isAccountPage && (
                <div className="resume-form__step-badge">
                    <span className="resume-form__step-badge-dot" />
                    Step 1 of 2 — Order Details
                </div>
            )}
            <h1 className="resume-form__title">Build your order</h1>
            <p className="resume-form__page-subtitle">Pick the career level, urgency and services that fit your goal</p>
        </div>

        <Section
            icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
            </svg>}
            title="Career Level"
            sub="Choose the stage that best matches your experience"
        >
            <CareerLevel careerLevel={options.careerLevel} />
        </Section>

        <Section
            icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
            </svg>}
            title="Urgency"
            sub="How soon do you need your order delivered?"
        >
            <Urgency urgency={options.urgency} />
        </Section>

        <Section
            icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
            </svg>}
            title="Services"
            sub="Pick at least one core service to get started"
            count={selectedServices.length}
            extraClass="resume-form__section--services"
            innerRef={servicesRef}
        >
            <ServicesGrid services={services} hasError={hasError} />
        </Section>

        <Section
            icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>}
            title="Features"
            sub="Optional upgrades to enhance your package"
            count={selectedFeatures.length}
        >
            <FeaturesGrid features={features} hasError={hasError} />
        </Section>

        {isAccountPage && (
            <Section
                icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                </svg>}
                title="Additional Information"
                sub="Upload files that help us learn more about your background (optional)"
            >
                <div ref={fileUploadRef} className={`resume-form__additional ${hasFileError ? 'resume-form__additional--error' : ''}`}>
                    <div className="resume-form__additional-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="17 8 12 3 7 8" />
                            <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                    </div>
                    <div className="resume-form__additional-content">
                        <div className="resume-form__additional-title">
                            Upload materials
                            <Tooltip text="Upload here your old resume or list of your advantages — everything that can help us to learn more about your professional background." />
                        </div>
                        <div className="resume-form__additional-subtitle">
                            {files.length > 0
                                ? `${files.length} file${files.length !== 1 ? 's' : ''} uploaded`
                                : 'Add your old resume, references, or any supporting documents'}
                        </div>
                    </div>
                    <button
                        className="resume-form__additional-btn"
                        type="button"
                        onClick={onOpenFileUpload || (() => dispatch(modalShow('fileUpload')))}
                    >
                        {files.length > 0 ? 'Add More' : 'Upload Files'}
                    </button>
                </div>
            </Section>
        )}

        <div className="resume-form__total-price">
            <div className="resume-form__price text-center">
                <strong className="label-price">Total</strong>
                <span className={`amount-price ${totalPrice <= 0 ? 'empty' : ''}`}>
                    <AnimatedNumber value={totalPrice} format={formatPrice} />
                </span>
            </div>
            <button className={`button-proceed ${hasError ? 'error' : ''}`} type="submit">
                {isAccountPage ? 'Place An Order' : 'Proceed'}
            </button>
        </div>
    </div>;
}
